import { Link, useParams } from "react-router-dom";
import researchResources from "../data/researchResources.json";
import { Seo } from "../components/Seo";

const SITE_URL = "https://www.roman-storyworld.link";

export function slugifyResourceTitle(title) {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isInternalPdf(url = "") {
  return url.startsWith("/") && /\.pdf(?:$|\?)/i.test(url);
}

export default function LibraryResource() {
  const { slug } = useParams();
  const resource = researchResources.find(
    (item) => isInternalPdf(item.url) && slugifyResourceTitle(item.title) === slug
  );

  if (!resource) {
    return (
      <main className="min-h-screen bg-stone-950 text-stone-200 px-6 py-16">
        <article className="max-w-4xl mx-auto">
          <Seo
            title="Research Report Not Found | Roman Storyworld"
            description="The requested Roman Storyworld research report could not be found."
            path={`/library/${slug}`}
          />
          <Link className="text-amber-300 hover:text-amber-200" to="/library">
            ← Return to the Roman Britain Library
          </Link>
          <h1 className="text-4xl font-bold text-amber-200 mt-8">Report not found</h1>
        </article>
      </main>
    );
  }

  const path = `/library/${slug}`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const pdfUrl = `${SITE_URL}${resource.url}`;
  const description = resource.description.replace(/^[^—]+—\s*/, "");
  const related = researchResources
    .filter(
      (item) =>
        item.id !== resource.id &&
        isInternalPdf(item.url) &&
        (item.category === resource.category ||
          item.tags?.some((tag) => resource.tags?.includes(tag)))
    )
    .slice(0, 4);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "ScholarlyArticle",
    headline: resource.title,
    description,
    url: canonicalUrl,
    inLanguage: "en-GB",
    author: { "@type": "Person", name: "Martin Antrobus" },
    publisher: {
      "@type": "Organization",
      name: "Roman Storyworld",
      url: `${SITE_URL}/`,
    },
    about: resource.tags || [],
    associatedMedia: {
      "@type": "MediaObject",
      contentUrl: pdfUrl,
      encodingFormat: "application/pdf",
    },
    isPartOf: {
      "@type": "CollectionPage",
      name: "Roman Britain Research Library",
      url: `${SITE_URL}/library`,
    },
  };

  return (
    <main className="min-h-screen bg-stone-950 text-stone-200 px-6 py-14">
      <Seo
        title={`${resource.title} | Roman Britain Research`}
        description={description}
        path={path}
        type="article"
        image={resource.backgroundImage
          ? `${SITE_URL}${resource.backgroundImage}`
          : undefined}
        jsonLd={articleSchema}
      />

      <article className="max-w-5xl mx-auto">
        <Link className="text-amber-300 hover:text-amber-200" to="/library">
          ← Return to the Roman Britain Library
        </Link>

        <header className="mt-8 border-b border-stone-800 pb-8">
          <p className="uppercase tracking-[0.18em] text-sm text-amber-400">
            {resource.category || "Roman Britain Research"}
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-amber-200 mt-3 leading-tight">
            {resource.title}
          </h1>
          <p className="text-lg md:text-xl text-stone-300 leading-8 mt-6 max-w-4xl">
            {description}
          </p>
          <p className="text-sm text-stone-400 mt-5">Research by Martin Antrobus</p>
        </header>

        <section className="py-9">
          <h2 className="text-3xl font-semibold text-amber-200">About this report</h2>
          <p className="mt-5 text-stone-300 leading-8 max-w-4xl">
            This Roman Storyworld research paper examines {resource.title.toLowerCase()} within
            the wider history, archaeology and landscape of Roman Britain. The complete report
            is available below as a PDF for reading, reference and further study.
          </p>

          {resource.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" aria-label="Subjects covered">
              {resource.tags.map((tag) => (
                <span key={tag} className="text-sm bg-stone-900 border border-stone-800 px-3 py-2 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex mt-8 bg-amber-700 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-lg transition"
          >
            Open the complete PDF report
          </a>
        </section>

        {related.length > 0 && (
          <section className="border-t border-stone-800 py-9">
            <h2 className="text-3xl font-semibold text-amber-200">Related research</h2>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/library/${slugifyResourceTitle(item.title)}`}
                  className="block bg-stone-900 border border-stone-800 hover:border-amber-600 rounded-xl p-5 transition"
                >
                  <h3 className="text-lg font-semibold text-amber-300">{item.title}</h3>
                  <p className="text-sm text-stone-400 leading-6 mt-2">{item.description}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}

